(() => {
  const elementName = 'parallax-editorial-collage';
  if (customElements.get(elementName)) return;

  class ParallaxEditorialCollage extends HTMLElement {
    static selectors = {
      canvas: '[data-parallax-canvas]',
      card: '[data-parallax-card]'
    };

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.canvas = this.querySelector(ParallaxEditorialCollage.selectors.canvas);
      this.cards = [...this.querySelectorAll(ParallaxEditorialCollage.selectors.card)];
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.desktopPointer = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.motionMode = this.dataset.motion || 'none';
      this.pointer = { x: 0, y: 0 };
      this.scrollProgress = 0;
      this.isVisible = true;

      if (!this.canvas || !this.cards.length || this.motionMode === 'none' || this.reducedMotion.matches) return;

      this.bindMotionEvents();
      this.observeVisibility();
      this.requestRender();
    }

    disconnectedCallback() {
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.visibilityObserver?.disconnect();
      window.cancelAnimationFrame(this.frameId);
    }

    bindMotionEvents() {
      if (this.usesPointer()) {
        this.addEventListener('pointermove', this.onPointerMove, { passive: true });
        this.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
      }

      if (this.usesScroll()) {
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      window.addEventListener('resize', this.onResize, { passive: true });
    }

    observeVisibility() {
      if (!('IntersectionObserver' in window)) return;
      this.visibilityObserver = new IntersectionObserver(([entry]) => {
        this.isVisible = entry.isIntersecting;
        if (this.isVisible) this.requestRender();
      }, { rootMargin: '15% 0px' });
      this.visibilityObserver.observe(this);
    }

    usesPointer() {
      return this.desktopPointer.matches && (this.motionMode === 'pointer' || this.motionMode === 'pointer_and_scroll');
    }

    usesScroll() {
      return this.motionMode === 'scroll' || this.motionMode === 'pointer_and_scroll';
    }

    onPointerMove = (event) => {
      const bounds = this.getBoundingClientRect();
      this.pointer.x = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
      this.pointer.y = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
      this.requestRender();
    };

    onPointerLeave = () => {
      this.pointer.x = 0;
      this.pointer.y = 0;
      this.requestRender();
    };

    onScroll = () => {
      const bounds = this.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      this.scrollProgress = Math.max(-1, Math.min(1, (viewportHeight / 2 - (bounds.top + bounds.height / 2)) / viewportHeight));
      this.requestRender();
    };

    onResize = () => {
      if (!this.desktopPointer.matches) this.pointer = { x: 0, y: 0 };
      this.onScroll();
    };

    requestRender() {
      if (!this.isVisible || this.frameId) return;
      this.frameId = window.requestAnimationFrame(() => {
        this.frameId = null;
        this.render();
      });
    }

    render() {
      const configuredStrength = Number.parseFloat(getComputedStyle(this).getPropertyValue('--pec-parallax-strength')) || 0;

      this.cards.forEach((card) => {
        const depth = Number.parseFloat(card.dataset.depth) || 0;
        const pointerX = this.usesPointer() ? this.pointer.x * configuredStrength * depth : 0;
        const pointerY = this.usesPointer() ? this.pointer.y * configuredStrength * depth : 0;
        const scrollY = this.usesScroll() ? this.scrollProgress * configuredStrength * depth * 1.4 : 0;
        card.style.setProperty('--pec-parallax-x', `${pointerX.toFixed(2)}px`);
        card.style.setProperty('--pec-parallax-y', `${(pointerY + scrollY).toFixed(2)}px`);
      });
    }
  }

  customElements.define(elementName, ParallaxEditorialCollage);
})();
